import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Users } from "lucide-react";
import { ApiURL } from "../../../setting/GlobleVariable";

/**
 * Free RSVP for organizer events. Name + email (no account needed); every
 * guest, plus-ones included, gets a QR pass by email that the door scanner
 * admits. POST /api/order/rsvp.
 */
export default function RsvpCard({ eventId, ticket, maxPlusOnes = 0, remaining = null, user }) {
  const [name, setName] = useState(user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "");
  const [email, setEmail] = useState(user?.email || "");
  const [plusOnes, setPlusOnes] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const full = remaining != null && remaining <= 0;
  const options = Array.from({ length: Math.max(0, Math.min(maxPlusOnes, 9)) + 1 }, (_, i) => i);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const token = (typeof localStorage !== "undefined" && localStorage.getItem("instapass_user_token")) || "offline";
      const res = await fetch(`${ApiURL}/order/rsvp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token },
        body: JSON.stringify({ eventId, ticketTypeId: ticket?.id, name, email, plusOnes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || "Couldn't save your RSVP. Please try again.");
      setDone(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6">
        <div className="mb-2 flex items-center gap-2 text-lg font-black text-white">
          <Check className="h-5 w-5 text-emerald-400" /> {done.alreadyRsvped ? "You're already on the list" : "You're going!"}
        </div>
        <p className="text-sm text-white/75">
          {done.ticketCount > 1 ? `${done.ticketCount} QR passes are` : "Your QR pass is"} on the way to <strong className="text-white">{email}</strong>. Show it at the door.
        </p>
        {user && (
          <Link to="/my-tickets" className="mt-4 inline-flex h-11 items-center rounded-xl bg-white px-5 text-sm font-bold text-[#0F172A]">
            Open My Tickets
          </Link>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#1F2937] bg-[#111827] p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-black uppercase tracking-tight text-white">RSVP</h2>
        <span className="flex items-center gap-1.5 text-xs text-white/60">
          <Users className="h-3.5 w-3.5" /> Free · {remaining == null ? "open to everyone" : full ? "full" : `${remaining} spots left`}
        </span>
      </div>
      {full ? (
        <p className="text-sm text-white/70">This event is full.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-white/60">Your name</span>
            <input required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className="h-12 w-full rounded-xl border border-white/10 bg-[#0B1120] px-4 text-white outline-none focus:border-red-500" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-white/60">Email (your pass goes here)</span>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className="h-12 w-full rounded-xl border border-white/10 bg-[#0B1120] px-4 text-white outline-none focus:border-red-500" />
          </label>
          {options.length > 1 && (
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-white/60">Bringing anyone?</span>
              <select value={plusOnes} onChange={(e) => setPlusOnes(Number(e.target.value))} className="h-12 w-full rounded-xl border border-white/10 bg-[#0B1120] px-4 text-white">
                {options.map((n) => (
                  <option key={n} value={n}>{n === 0 ? "Just me" : `Me + ${n}`}</option>
                ))}
              </select>
            </label>
          )}
          <div className={`flex items-end ${options.length > 1 ? "" : "sm:col-span-2"}`}>
            <button type="submit" disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-red-600 font-black uppercase tracking-wide text-white hover:bg-red-500 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} I'm going
            </button>
          </div>
        </div>
      )}
      {error && <p role="alert" className="mt-3 text-sm text-red-400">{error}</p>}
    </form>
  );
}
